import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import view from '../views/transactions.js';
import form from '../forms/transactions.js';
import move_location_bulk_form from '../forms/move-location-bulk.js';
import * as types from '../types.js';
import { formatTransactionEndpointSync } from '../utils/format-transaction-endpoint.js';

function escapeCsvCell(value)
{
	if (value == null || value === '')
		return '';
	const s = String(value);
	if (/[";\r\n]/.test(s))
		return '"' + s.replace(/"/g, '""') + '"';
	return s;
}

/** CSV separado por ; (compatível com Excel PT-BR). */
function toCsvSemicolon(rows, headers)
{
	const line = (cells) => cells.map(escapeCsvCell).join(';');
	const h = line(headers.map(h => h.label));
	const body = rows.map((row) =>
		line(headers.map(h =>
		{
			const v = row[h.field];
			if (v != null && typeof v === 'object')
				return JSON.stringify(v);
			return v;
		})));
	return [h, ...body].join('\r\n');
}

function downloadText(text, filename, mime)
{
	const blob = new Blob(['\ufeff' + text], { type: mime });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename;
	a.click();
	URL.revokeObjectURL(a.href);
}

function normAddressForFilter(s)
{
	return String(s ?? '').replace(/[\s\-]+/g, '').toLowerCase();
}

/** Id da posição: número ou endereço (ex.: S-1 / s-1); usa o mesmo critério de filtro `address` na API. */
async function resolveLocationIdForMove(companyId, warehouseId, raw)
{
	const rawStr = String(raw ?? '').trim();
	if (!rawStr)
		return NaN;
	const n = Number(rawStr);
	if (Number.isInteger(n) && n >= 1)
		return n;
	const term = normAddressForFilter(rawStr);
	if (!term)
		return NaN;
	const wh = Number(warehouseId);
	if (!companyId || !Number.isInteger(wh) || wh < 1)
		return NaN;
	const filter = `company_id*eq*${companyId},warehouse_id*eq*${wh},address*co*${term}`;
	const q = `?filter=${encodeURIComponent(filter)}&limit=80&sort=4`;
	const rows = await api.call(api.list_locations, q);
	if (!Array.isArray(rows) || rows.length === 0)
		return NaN;
	if (rows.length === 1)
		return Number(rows[0].id);
	const want = normAddressForFilter(rawStr);
	for (const r of rows)
	{
		const addr = Array.isArray(r.address) ? r.address.join('-') : String(r.address ?? '');
		if (normAddressForFilter(addr) === want)
			return Number(r.id);
	}
	return NaN;
}

const movements = function()
{
	let movements_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_locations, this.queryAll()).then((data) => db.set('locations', data).catch(error => notify(error)));
			api.call(api.list_products, this.queryAll()).then((data) => db.set('products', data).catch(error => notify(error)));
			api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));
			api.call(api.list_users, this.queryAll()).then((data) => db.set('users', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_transactions, query);
		},
		async loadRecord(id)
		{
			return await api.call(api.select_transaction, ...JSON.parse(id));
		},
		async saveRecord(data, old_data)
		{
			let _record = {
				product_id: Number(data.product_id),
				lot_id: Number(data.lot_id),
				source_type: data.source_type,
				target_type: data.target_type,
				source: JSON.parse(data.source),
				target: JSON.parse(data.target),
				quantity: Number(data.quantity),
				value: Number(data.value),
			};
			if (old_data)
				_record.movement_kind = data.movement_kind || null;
			else if (data.movement_kind)
				_record.movement_kind = data.movement_kind;
			if (old_data)
				return await api.call(api.update_transaction, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_transaction, data.company_id, _record);
		},
		async _exportMovementsDelimited(filename, title)
		{
			const rows = await this.queryFilteredData();
			if (!rows.length)
			{
				notify('Nenhum registro no filtro.', 'Exportar');
				return;
			}
			const headers = [
				{ field: 'company_id', label: 'Empresa (id)' },
				{ field: 'id', label: 'Movimentação' },
				{ field: 'product_id', label: 'Produto' },
				{ field: 'lot_id', label: 'Lote' },
				{ field: 'user_id', label: 'Usuário' },
				{ field: 'date_time', label: 'Data/Hora' },
				{ field: 'source_type', label: 'Tipo origem' },
				{ field: 'target_type', label: 'Tipo destino' },
				{ field: '_origem_fmt', label: 'Origem' },
				{ field: '_destino_fmt', label: 'Destino' },
				{ field: 'quantity', label: 'Quantidade' },
				{ field: 'movement_kind', label: 'Natureza' },
				{ field: 'value', label: 'Valor' },
			];
			const mapped = rows.map((r) => ({
				...r,
				_origem_fmt: formatTransactionEndpointSync(r.company_id, r.source_type, r.source),
				_destino_fmt: formatTransactionEndpointSync(r.company_id, r.target_type, r.target),
			}));
			const csv = toCsvSemicolon(mapped, headers);
			downloadText(csv, filename, 'text/csv;charset=utf-8');
			notify('Download iniciado.', title);
		},
		async exportMovementsCsv()
		{
			ask(`Exportar todos os registros do filtro atual em CSV (separador ;)?`, 'Exportar CSV', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				await this._exportMovementsDelimited(
					`movimentacoes_${new Date().toISOString().slice(0, 10)}.csv`,
					'CSV'
				);
			});
		},
		async exportMovementsExcel()
		{
			ask(`Exportar registros do filtro para Excel (arquivo .xls com separador ;)?`, 'Exportar Excel', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				await this._exportMovementsDelimited(
					`movimentacoes_${new Date().toISOString().slice(0, 10)}.xls`,
					'Excel'
				);
			});
		},
		async printMovementsPdf()
		{
			const rows = await this.queryFilteredData();
			if (!rows.length)
			{
				notify('Nenhum registro no filtro.', 'Imprimir');
				return;
			}
			const w = window.open('', '_blank');
			if (!w)
			{
				notify('Permita pop-ups para gerar o PDF.', 'Imprimir');
				return;
			}
			const mkOpt = types.opt_movement_kind();
			const mkLabel = (k) =>
			{
				const o = mkOpt.options.find(x => x.value === k);
				return o ? o.label : (k || '');
			};
			let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Movimentações</title>
			<style>
				body { font-family: Arial, sans-serif; margin: 1.2rem; }
				h1 { font-size: 1.1rem; }
				table { border-collapse: collapse; width: 100%; font-size: 0.75rem; }
				th, td { border: 1px solid #444; padding: 0.25rem 0.4rem; text-align: left; }
				th { background: #eee; }
			</style></head><body><h1>Movimentações (filtrado)</h1><table><thead><tr>
			<th>Id</th><th>Data/Hora</th><th>Natureza</th><th>Qtd</th><th>Produto</th><th>Lote</th><th>Origem</th><th>Destino</th>
			</tr></thead><tbody>`;
			for (const r of rows.slice(0, 2000))
			{
				html += '<tr>';
				html += `<td>${escapeCsvCell(r.id)}</td>`;
				html += `<td>${escapeCsvCell(r.date_time)}</td>`;
				html += `<td>${escapeCsvCell(mkLabel(r.movement_kind))}</td>`;
				html += `<td>${escapeCsvCell(r.quantity)}</td>`;
				html += `<td>${escapeCsvCell(r.product_id)}</td>`;
				html += `<td>${escapeCsvCell(r.lot_id)}</td>`;
				html += `<td>${escapeCsvCell(formatTransactionEndpointSync(r.company_id, r.source_type, r.source))}</td>`;
				html += `<td>${escapeCsvCell(formatTransactionEndpointSync(r.company_id, r.target_type, r.target))}</td>`;
				html += '</tr>';
			}
			html += '</tbody></table>';
			if (rows.length > 2000)
				html += `<p>… e mais ${rows.length - 2000} linhas (limite de impressão). Use exportação CSV completa.</p>`;
			html += '<script>window.onload=function(){window.print();}<\/script></body></html>';
			w.document.write(html);
			w.document.close();
		},
		moveLocationBulk()
		{
			const crud = this;
			let fs;
			const sendRequest = async (data) =>
			{
				const co = data.warehouse_company_id;
				if (!co)
				{
					notify('Selecione a empresa.', 'Mover posição inteira');
					return;
				}
				const sw = Number(data.source_warehouse_id);
				const tw = Number(data.target_warehouse_id);
				if (!Number.isInteger(sw) || sw < 1 || !Number.isInteger(tw) || tw < 1)
				{
					notify('Selecione armazém de origem e destino na lista (após escolher a empresa).', 'Mover posição inteira');
					return;
				}
				let sl = Number(data.source_location_id);
				let tl = Number(data.target_location_id);
				if (!Number.isInteger(sl) || sl < 1)
					sl = await resolveLocationIdForMove(co, sw, data.source_location_id);
				if (!Number.isInteger(tl) || tl < 1)
					tl = await resolveLocationIdForMove(co, tw, data.target_location_id);
				if (!Number.isInteger(sl) || sl < 1)
				{
					notify('Posição de origem inválida ou ambígua. Use a lista (endereço após o armazém) ou o id numérico da posição.', 'Mover posição inteira');
					return;
				}
				if (!Number.isInteger(tl) || tl < 1)
				{
					notify('Posição de destino inválida ou ambígua. Use a lista (endereço após o armazém) ou o id numérico da posição.', 'Mover posição inteira');
					return;
				}
				const payload = {
					target_warehouse_id: tw,
					target_location_id: tl,
					reason: (data.reason || '').trim() || undefined,
				};
				if (data.movement_kind && String(data.movement_kind).trim())
					payload.movement_kind = String(data.movement_kind).trim();
				const out = await api.call(
					api.move_location_all_available,
					co,
					sw,
					sl,
					payload
				);
				if (!out)
					return;
				const n = out.lines_moved ?? 0;
				const q = out.quantity_total ?? 0;
				notify(
					n
						? `Movimentadas ${n} linha(s), ${q} unidade(s). Transações: ${(out.transaction_ids || []).length}.`
						: 'Nenhuma linha com saldo disponível na posição de origem.',
					'Mover posição inteira'
				);
				fs.destroy();
				crud.loadPage();
			};
			fs = formset.create(move_location_bulk_form, sendRequest);
			fs.init();
		},
		async deleteMovement()
		{
			const ids = this.getSelection();
			if (!ids.length)
			{
				notify('Selecione ao menos uma movimentação.', 'Excluir');
				return;
			}
			ask(`Excluir ${ids.length} movimentação(ões)?`, 'Confirmar', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				for (const raw of ids)
				{
					const [company_id, id] = JSON.parse(raw);
					const r = await api.call(api.delete_transaction, company_id, id);
					if (r === undefined)
						return;
				}
				notify('Registro(s) excluído(s).', 'Excluir');
				this.clearGridSelection();
				this.loadPage();
			});
		},
	});

	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Movimentação', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador' },
		{ index: 3, field: 'product_id', label: 'Produto', types: [types.datalist()], placeholder: '', title: 'Produto', store: 'products', labels: ['name'] },
		{ index: 4, field: 'lot_id', label: 'Lote', types: [types.number(0, 2)], placeholder: '0', title: 'Lote' },
		{ index: 5, field: 'user_id', label: 'Usuário', types: [types.datalist()], placeholder: '', title: 'Usuário', store: 'users', labels: ['name'] },
		{ index: 6, field: 'date_time', label: 'Data/Hora', types: [types.datetime()], placeholder: '', title: 'Data e hora' },
		{ index: 7, field: 'source_type', label: 'Tipo Origem', types: [types.opt_transaction_type(), types.datalist()], placeholder: '', title: 'Tipo de origem' },
		{ index: 8, field: 'target_type', label: 'Tipo Destino', types: [types.opt_transaction_type(), types.datalist()], placeholder: '', title: 'Tipo de destino' },
		{ index: 9, field: 'source', label: 'Origem', types: [], placeholder: '', title: 'Origem' },
		{ index: 10, field: 'target', label: 'Destino', types: [], placeholder: '', title: 'Destino' },
		{ index: 11, field: 'quantity', label: 'Quantidade', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade' },
		{ index: 12, field: 'movement_kind', label: 'Natureza', types: [types.opt_movement_kind()], placeholder: '', title: 'MOV / RESUP / INV / BLOQ / UNPICK' },
		{ index: 13, field: 'value', label: 'Valor', types: [types.number(2)], placeholder: '0,00', title: 'Valor' },
	];

	let operations = [
		{ hash: '#move-location-bulk', label: 'Mover&nbsp;posição&nbsp;inteira' },
		{ hash: '#delete-movements', label: 'Excluir' },
	];
	let reports = [
		{ hash: '#export-movements-csv', label: 'Exportar&nbsp;CSV' },
		{ hash: '#export-movements-excel', label: 'Exportar&nbsp;Excel' },
		{ hash: '#print-movements-pdf', label: 'Imprimir&nbsp;(PDF)' },
	];

	router.set('#move-location-bulk', movements_crud.moveLocationBulk.bind(movements_crud));
	router.set('#delete-movements', movements_crud.deleteMovement.bind(movements_crud));
	router.set('#export-movements-csv', movements_crud.exportMovementsCsv.bind(movements_crud));
	router.set('#export-movements-excel', movements_crud.exportMovementsExcel.bind(movements_crud));
	router.set('#print-movements-pdf', movements_crud.printMovementsPdf.bind(movements_crud));

	movements_crud.run(
		'movements',
		'Movimentações',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);
};

export default movements;
